import { css, html } from 'react-strict-dom';
import { Style } from 'src/constants';
import { color } from 'src/constants/color.css';
import { useTimer, useTimerElapsed } from 'src/contexts';
import { formatElapsed } from 'src/util';

import MaybeLink from './MaybeLink';
import Text from './Text';
import TimerButton from './TimerButton';
import TimerCompanyLogo from './TimerCompanyLogo';
import TimerError from './TimerError';

/**
 * The timer as it sits at the top of the side menu: what is on the clock, and
 * a single control to start or stop it.  Stays out of the way until there is
 * some work to point at, since there would be nothing to start.
 */
export default function TimerSideMenu() {
  const { running, runningTarget, lastTarget, lastCategory, actionError } =
    useTimer();

  const target = runningTarget ?? lastTarget;
  // A failed start with nothing timed yet still has to say so.
  if (!target) return actionError ? <ErrorSection /> : null;

  return (
    <html.div style={styles.section}>
      <html.div
        style={[styles.card, running ? styles.cardRunning : styles.cardStopped]}
      >
        <html.div style={[Style.row, Style.alignCenter, styles.row]}>
          <html.span
            style={[
              styles.dot,
              running ? styles.dotRunning : styles.dotStopped,
            ]}
          />
          <TimerCompanyLogo company={target.company} title={target.title} />

          {/* Only the reference itself links out — the clock beside it isn't
              part of what you'd click to open the work. */}
          <html.span style={[Style.row, Style.alignCenter, styles.refGroup]}>
            {target.reference && (
              <MaybeLink to={target.path} fill>
                <Text
                  typeface="caption.medium"
                  color={running ? 'green' : 'neutral60'}
                  style={styles.reference}
                >
                  {target.reference}
                </Text>
              </MaybeLink>
            )}
            {running && <Elapsed separated={!!target.reference} />}
          </html.span>

          <html.span style={Style.fill} />

          <TimerButton
            target={target}
            tone={running ? 'subtle' : 'solid'}
            category={lastCategory}
          />
        </html.div>

        {/* Greyed rather than swapped for a placeholder, so the work on deck
            stays readable while nothing is running. */}
        <MaybeLink to={target.path} fill>
          <html.span style={styles.title}>
            <Text
              typeface="body.small.medium"
              color={running ? 'neutral100' : 'neutral60'}
            >
              {target.title}
            </Text>
          </html.span>
        </MaybeLink>
      </html.div>

      <TimerError />
    </html.div>
  );
}

function ErrorSection() {
  return (
    <html.div style={styles.section}>
      <TimerError />
    </html.div>
  );
}

function Elapsed({ separated }: { separated: boolean }) {
  const elapsedSeconds = useTimerElapsed();
  const clock = formatElapsed(elapsedSeconds, { compact: true });

  return (
    <Text typeface="caption.medium" color="green" style={styles.clock}>
      {separated ? `· ${clock}` : clock}
    </Text>
  );
}

const styles = css.create({
  // Matches the nav's own sections, so the timer sits on the same grid.
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    paddingBlock: 16,
    paddingInline: 12,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    borderRadius: 8,
    paddingBlock: 10,
    paddingInline: 10,
  },
  cardRunning: {
    // The whole card carries the "logging right now" signal, so it reads at a
    // glance from the corner of the eye.
    backgroundColor: color.greenBg,
  },
  cardStopped: {
    backgroundColor: color.neutral5,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
  },
  row: {
    gap: 6,
  },
  dot: {
    flexShrink: 0,
    width: 8,
    height: 8,
    borderRadius: 999,
  },
  dotRunning: {
    backgroundColor: color.green,
  },
  dotStopped: {
    backgroundColor: color.neutral40,
  },
  refGroup: {
    gap: 4,
    minWidth: 0,
  },
  // A category reference runs much longer than a ticket's `#607`, so it gives
  // way to the clock rather than pushing it out of the 200px column.
  reference: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  clock: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  title: {
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
  },
});
